import SurveyRecipients from "./SurveyRecipients.vue";
import SurveyResults from "./SurveyResults.vue";
import SurveyQuestionsList from "./SurveyQuestionsList.vue";

const statusLabels = {
  draft: "Rascunho",
  sending: "Enviando",
  sent: "Enviada",
  closed: "Encerrada",
};

const statusClass = {
  draft: "bg-secondary",
  sending: "bg-warning text-dark",
  sent: "bg-success",
  closed: "bg-dark",
};

// Enquanto a fila de envio roda, a tela se atualiza sozinha (doc:
// "vale a tela de detalhe atualizar sozinha enquanto o status for sending").
const POLL_INTERVAL_MS = 5000;

export default {
  name: "SurveyDetail",
  components: { SurveyRecipients, SurveyResults, SurveyQuestionsList },
  data() {
    return {
      uuid: this.$route.params.uuid,
      loading: false,
      survey: null,
      activeTab: "results",
      recipientsLoaded: false,
      resultsLoaded: false,
      pollTimer: null,
      sending: false,
      closing: false,
    };
  },
  computed: {
    isDraft() {
      return this.survey?.status === "draft";
    },
    isSent() {
      return this.survey?.status === "sent";
    },
    responseRate() {
      if (!this.survey?.audience_count) return 0;
      return Math.round(((this.survey.answered || 0) / this.survey.audience_count) * 1000) / 10;
    },
  },
  mounted() {
    this.load();
  },
  beforeDestroy() {
    clearTimeout(this.pollTimer);
  },
  methods: {
    async load() {
      this.loading = true;
      try {
        const res = await this.$surveyAdminService.show(this.uuid);
        this.survey = res.data;
        if (this.survey.status === "sending") {
          clearTimeout(this.pollTimer);
          this.pollTimer = setTimeout(() => this.load(), POLL_INTERVAL_MS);
        }
      } catch (e) {
        this.$eventBus.$emit("showToast", {
          title: "Erro",
          msg: e.message || "Erro ao carregar a pesquisa.",
        });
      } finally {
        this.loading = false;
      }
    },
    selectTab(tab) {
      this.activeTab = tab;
      if (tab === "recipients") this.recipientsLoaded = true;
      if (tab === "results") this.resultsLoaded = true;
    },
    editSurvey() {
      this.$router.push(`${this.$paths.surveys.edit}/${this.uuid}`);
    },
    deleteSurvey() {
      this.$eventBus.$emit("showConfirmationAlert", {
        title: "Excluir este rascunho?",
        message: "Essa pesquisa ainda não foi enviada. Deseja excluir o rascunho?",
        confirmCallback: async () => {
          try {
            await this.$surveyAdminService.destroy(this.uuid);
            this.$eventBus.$emit("showToast", {
              title: "Rascunho excluído",
              msg: "A pesquisa foi excluída.",
            });
            this.$router.push(this.$paths.surveys.list);
          } catch (e) {
            this.$eventBus.$emit("showToast", {
              title: "Erro",
              msg: e.message || "Erro ao excluir a pesquisa.",
            });
          }
        },
      });
    },
    sendSurvey() {
      this.$eventBus.$emit("showConfirmationAlert", {
        title: "Enviar esta pesquisa?",
        message: `Ela será enviada por e-mail para ${this.survey.audience_count} pessoa(s). Essa ação não pode ser desfeita.`,
        confirmCallback: async () => {
          this.sending = true;
          try {
            await this.$surveyAdminService.send(this.uuid);
            this.$eventBus.$emit("showToast", {
              title: "Envio iniciado",
              msg: "A pesquisa está sendo enviada.",
            });
            this.load();
          } catch (e) {
            this.$eventBus.$emit("showToast", {
              title: "Erro",
              msg: e.message || "Erro ao enviar a pesquisa.",
            });
          } finally {
            this.sending = false;
          }
        },
      });
    },
    closeSurvey() {
      this.$eventBus.$emit("showConfirmationAlert", {
        title: "Encerrar esta pesquisa?",
        message: "Ninguém mais vai conseguir responder depois disso.",
        confirmCallback: async () => {
          this.closing = true;
          try {
            await this.$surveyAdminService.close(this.uuid);
            this.$eventBus.$emit("showToast", {
              title: "Pesquisa encerrada",
              msg: "A pesquisa não aceita mais respostas.",
            });
            this.load();
          } catch (e) {
            this.$eventBus.$emit("showToast", {
              title: "Erro",
              msg: e.message || "Erro ao encerrar a pesquisa.",
            });
          } finally {
            this.closing = false;
          }
        },
      });
    },
    statusLabel(status) {
      return statusLabels[status] || status;
    },
    statusClass(status) {
      return statusClass[status] || "bg-secondary";
    },
    formatDate(date) {
      return date ? this.$moment(date).format("DD/MM/YYYY HH:mm") : "-";
    },
    // Texto legível do filtro de público, pra prévia não mostrar só JSON.
    audienceSummary() {
      const f = this.survey?.audience_filters || {};
      const parts = [];
      parts.push(f.user_type === "psychologist" ? "Psicólogos" : "Pacientes");
      if (f.created_from) parts.push(`cadastro de ${this.formatDate(f.created_from)}`);
      if (f.created_to) parts.push(`até ${this.formatDate(f.created_to)}`);
      if (f.without_appointments) parts.push("sem nenhuma consulta concluída");
      if (f.last_appointment_from) {
        parts.push(`última consulta de ${this.formatDate(f.last_appointment_from)}`);
      }
      if (f.last_appointment_to) parts.push(`até ${this.formatDate(f.last_appointment_to)}`);
      if (f.subscription) {
        parts.push(f.subscription === "subscriber" ? "assinantes" : "não assinantes");
      }
      if (f.approved === true) parts.push("aprovados");
      if (f.approved === false) parts.push("não aprovados");
      return parts.join(" · ");
    },
  },
};
